/**
 * Production-facing discovery contracts (PROVISIONAL ICM until confirmed).
 * Demo mock implements these; UI must not import mock modules directly.
 */

export type PresidioDiscoveryPrimaryRole = 'standard' | 'administrator';

export type PresidioDiscoveryPurchaseRuleType = 'OrderSubtotal' | 'LinePrice' | 'Sku';

export interface PresidioDiscoveryAffiliate {
  customerNo: string;
  companyName: string;
}

export interface PresidioDiscoveryUser {
  login: string;
  firstName: string;
  lastName: string;
  email: string;
  primaryRole: PresidioDiscoveryPrimaryRole;
  /** Approver is an additional permission, not a primary role. */
  canApprove: boolean;
  status: 'Active' | 'Pending' | 'Disabled';
  /**
   * Assigned accounts this user may access (home and/or affiliates).
   * Every user must have at least one. Never auto-grant every affiliate.
   */
  affiliateCustomerNos: string[];
  /** Login of assigned single-step approver (optional). */
  approverLogin?: string;
  /** Optional profile fields collected on invite / edit (MVP 1.2.1). */
  department?: string;
  title?: string;
  /** Digits-only phone when provided. */
  phone?: string;
}

export interface PresidioDiscoveryPurchaseRule {
  id: string;
  userLogin: string;
  type: PresidioDiscoveryPurchaseRuleType;
  threshold?: number;
  sku?: string;
}

export interface PresidioDiscoveryAddressMeta {
  id: string;
  customerNo: string;
  kind: 'billing' | 'shipping';
  hidden: boolean;
  label: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  mainDivisionCode: string;
  postalCode: string;
  country: string;
  recentlyUsed?: boolean;
}

export interface PresidioDiscoveryAccessRequest {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  companyName: string;
  requestedRole: PresidioDiscoveryPrimaryRole;
  routeTo: 'administrators' | 'sales_rep';
  reviewerLabel: string;
  createdAt: string;
  status: 'Pending';
  department?: string;
  title?: string;
  phone?: string;
}

export interface PresidioDiscoveryCatalogConfig {
  customerNo: string;
  allowedSkus: string[];
  negotiatedPrices: Record<string, number>;
  pricingNotes: string[];
}

/**
 * Approved email domains for a customer / affiliate account (MVP 1.2.1).
 * Empty `domains` = all email domains accepted when creating users for that account.
 */
export interface PresidioDiscoveryApprovedEmailDomains {
  customerNo: string;
  domains: string[];
}

export interface PresidioDiscoveryState {
  users: PresidioDiscoveryUser[];
  purchaseRules: PresidioDiscoveryPurchaseRule[];
  addressMeta: PresidioDiscoveryAddressMeta[];
  accessRequests: PresidioDiscoveryAccessRequest[];
  catalogByCustomer: Record<string, PresidioDiscoveryCatalogConfig>;
  pricingPrecedence: string[];
  /**
   * Approved email domains keyed by customer / affiliate number.
   * Missing or empty list → any email domain is accepted for that account.
   */
  approvedEmailDomainsByCustomer: Record<string, string[]>;
  /**
   * DEMO / MVP 1.2.4: orders held for purchase-rule approval.
   * Backs My Account “Orders awaiting approval” / Approvals (DIAS requisition routes).
   */
  pendingApprovalOrders: PresidioDiscoveryPendingApprovalOrder[];
}

export type PresidioDiscoveryPendingApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface PresidioDiscoveryPendingApprovalOrder {
  id: string;
  /** Shopper-facing request number (maps to requisitionNo in DIAS payloads). */
  requisitionNo: string;
  orderNo?: string;
  userLogin: string;
  approverLogin?: string;
  reason: string;
  createdAt: string;
  status: PresidioDiscoveryPendingApprovalStatus;
  approvalDate?: number;
  approvalComment?: string;
  totalGross: number;
  totalNet: number;
  currency: string;
  lineItemCount: number;
  /** Optional ICM-shaped detail payload for the approval detail page. */
  detailPayload?: unknown;
}

export interface PresidioDiscoverySkuOption {
  sku: string;
  name: string;
}

/** Affilliate accounts available in the discovery prototype / expected ICM set. */
export const PRESIDIO_DISCOVERY_AFFILIATES: readonly PresidioDiscoveryAffiliate[] = [
  { customerNo: 'PRESIDIO-MOCK', companyName: 'Acme Healthcare' },
  { customerNo: 'PRESIDIO-AFF-BETA', companyName: 'Beta Manufacturing' },
  { customerNo: 'PRESIDIO-AFF-GAMMA', companyName: 'Gamma Education' },
];
